import React from "react";
import { Box, Link } from "@mui/material";
import {
  LocalShipping,
  Air,
  Build,
  LocalGasStation,
  DirectionsBus,
  Gavel,
  Business,
  Factory,
  Flag,
  EvStation,
  BatteryFull,
  RouteOutlined,
  Map,

  Forest,
  AccountBalanceWallet,
} from "@mui/icons-material";

import HeroSection from "../components/HeroSection";
import ContentSection from "../components/ContentSection";

import WhiteTrucksOnTheRoad from "../assets/images/WhiteTrucksOnTheRoad.jpg";
import TruckFueling from "../assets/images/TruckFueling.jpg";
import TruckBottomAngleView from "../assets/images/TruckBottomAngleView.jpg";
import TruckBlurredBg from "../assets/images/TruckBlurredBg.jpg";
import AerialViewWoodsAndTrucks from "../assets/images/AerialViewWoodsAndTrucks.jpg";

import StyledCard from "../components/StyledCard";
import Label from "../components/ui/Label";
import SectionDescription from "../components/SectionDescription";
import Heading from "../components/ui/Heading";
import HalvedImageSection from "../components/HalvedImageSection";
import HalvedContentSection from "../components/HalvedContentSection";
import GridLayout from "../components/GridLayout";
import UltraWideBgSection from "../components/UltraWideBgSection";
import ImageCard from "../components/ImageCard";

import { CompanyInfo } from "./constants";

const KeyBenefitsOfBioLNG = Object.freeze([
  {
    title: "Significant CO₂ Reduction",
    description:
      "Using BioLNG can lower CO₂ emissions by up to 100% compared to diesel when sourced from agricultural waste.",
    icon: Forest,
  },
  {
    title: "Drop-in Compatibility",
    description:
      "BioLNG can be seamlessly integrated into existing LNG infrastructure, making it an immediate and scalable solution.",
    icon: Build,
  },
  {
    title: "Long Driving Range",
    description:
      "LNG-powered trucks can travel over 1,000 km on a single tank, making BioLNG ideal for long-haul applications.",
    icon: LocalShipping,
  },
  {
    title: "Lower Total Cost of Ownership (TCO)",
    description:
      "Compared to electric alternatives, BioLNG trucks offer competitive operating costs due to lower fuel expenses and reduced taxation in many countries.",
    icon: AccountBalanceWallet,
  },
  {
    title: "Reduction of Air Pollutants",
    description:
      "BioLNG-fueled trucks emit significantly lower levels of nitrogen oxides (NOx) and particulate matter (PM), improving air quality.",
    icon: Air,
  },
]);

const MarketTrendsInBioLNG = Object.freeze([
  {
    title: "Growing Refueling Infrastructure",
    description:
      "There are over 700 LNG filling stations in Europe, with numbers expected to exceed 2,000 by 2030.",
    icon: LocalGasStation,
  },
  {
    title: "Fleet Expansion",
    description:
      "The number of LNG-powered trucks in Europe is projected to reach 280,000 by 2030, with BioLNG expected to account for 40% of their fuel.",
    icon: DirectionsBus,
  },
  {
    title: "Government Incentives",
    description:
      "Policies such as biomethane Feed-in Tariffs (FiT) and subsidies covering up to 50% of the cost difference between diesel and LNG trucks are being introduced to encourage adoption.",
    icon: Gavel,
  },
  {
    title: "Corporate Adoption",
    description:
      "Companies like LIDL and CEZ are already integrating BioLNG into their logistics operations, signaling a shift toward sustainable supply chains.",
    icon: Business,
  },
]);

const InfrastructureDevelopmentsInBioLNG = Object.freeze([
  {
    title: "LNG Network Expansion",
    description:
      "GasNet and Eurowag are expanding their LNG distribution networks in Central Europe.",
    icon: Map,
  },
  {
    title: "Scaling BioLNG Production",
    description:
      "Germany and the Netherlands are leading the way in BioLNG adoption, with dedicated liquefaction plants scaling up production capacity.",
    icon: Factory,
  },
  {
    title: "Growing Domestic Production",
    description:
      "Czech Republic and Poland are expected to see increased BioLNG availability as domestic production incentives take effect.",
    icon: Flag,
  },
]);

const ChallengesOfEVsInFreight = Object.freeze([
  {
    title: "Range Limitations",
    description:
      "EVs struggle to meet the high mileage demands of freight transport, making them less viable for long-haul applications.",
    icon: RouteOutlined,
  },
  {
    title: "Charging Infrastructure",
    description:
      "Charging networks remain inadequate for long-haul operations, limiting the practicality of electric trucks.",
    icon: EvStation,
  },
  {
    title: "Battery Weight",
    description:
      "Heavy-duty electric trucks require large, heavy batteries, which reduce payload capacity and overall efficiency.",
    icon: BatteryFull,
  },
]);

export default function BioLngTransportPage() {
  return (
    <>
      <HeroSection
        badge="The Future of Sustainable Mobility"
        backgroundImage={WhiteTrucksOnTheRoad}
        heading="BioLNG in Transport"
      >
        <SectionDescription color="white">
          The transition to sustainable transport is a crucial part of global
          decarbonisation efforts, and BioLNG (Bio-Liquefied Natural Gas) is
          emerging as a key solution for long-haul transport. With the European
          Union aiming for climate neutrality by 2050, BioLNG presents an
          opportunity to significantly cut greenhouse gas (GHG) emissions while
          leveraging existing LNG infrastructure. This makes it an attractive
          and scalable alternative for reducing carbon footprints in heavy-duty
          transport.
        </SectionDescription>
      </HeroSection>
      <ContentSection>
        <HalvedImageSection
          image={AerialViewWoodsAndTrucks}
          imagePosition="right"
          content={
            <>
              <Heading level="h2">Why BioLNG?</Heading>
              <SectionDescription marginTop={2}>
                BioLNG is a renewable fuel derived from biomethane, which is
                produced from organic waste sources such as agricultural
                residues, wastewater, and municipal waste. Unlike fossil fuels,
                BioLNG is nearly carbon-neutral and can even result in negative
                emissions when produced from manure, as it captures methane that
                would otherwise be released into the atmosphere.
              </SectionDescription>
            </>
          }
        />
      </ContentSection>
      <ContentSection marginTop={{xs: undefined, lg: 6}} marginBottom={6}>
        <Box textAlign={{ xs: "left", lg: "center" }}>
          <Heading level="h3" marginTop={2}>
            Key Benefits of BioLNG for Long-Haul Transport
          </Heading>
        </Box>
        <Box
          marginTop={4}
          display="flex"
          gap={2}
          flexDirection={{ xs: "column", lg: "row" }}
        >
          {KeyBenefitsOfBioLNG.map((item) => {
            return (
              <Box key={item.title} flex="1">
                <StyledCard {...item} dark />{" "}
              </Box>
            );
          })}
        </Box>
      </ContentSection>
      <UltraWideBgSection marginTop={4}>
        <ContentSection>
          <HalvedContentSection
            leftContent={
              <>
                <Label>Current and Future Market Trends</Label>
                <Heading level="h4" marginTop={2}>
                  Market Growth and Future Projections
                </Heading>
                <SectionDescription marginTop={2}>
                  The BioLNG market is expanding across Europe, driven by
                  regulatory policies such as the Renewable Energy Directive
                  (RED III), CO₂ emission reduction targets, and carbon credit
                  systems. The demand for BioLNG in transport is expected to
                  reach 461 TWh by 2030, representing nearly 50% of total
                  biomethane production in Europe.
                </SectionDescription>
              </>
            }
            rightContent={
              <GridLayout
                items={MarketTrendsInBioLNG.map((item) => {
                  return <StyledCard {...item} key={item.title} />;
                })}
                itemsPerRow={2}
              />
            }
          />
        </ContentSection>
      </UltraWideBgSection>
      <UltraWideBgSection backgroundColor="white">
        <ContentSection>
          <HalvedContentSection
            rightContent={
              <>
                <Label>Key Developments in Infrastructure</Label>
                <Heading level="h3" marginTop={2}>
                  Infrastructure Development and Logistics Integration
                </Heading>
                <SectionDescription marginTop={2}>
                  The rapid expansion of LNG refueling stations across Europe is
                  a strong indicator of BioLNG’s viability in heavy-duty
                  transport. Major logistics companies and fleet operators are
                  increasingly investing in LNG and BioLNG-compatible vehicles,
                  recognizing the long-term cost savings and sustainability
                  benefits.
                </SectionDescription>
              </>
            }
            leftContent={
              <GridLayout
                items={[
                  <ImageCard key="image" image={TruckFueling} />,
                  ...InfrastructureDevelopmentsInBioLNG.map((item) => {
                    return <StyledCard {...item} key={item.title} />;
                  }),
                ]}
                itemsPerRow={2}
              />
            }
            reverseMobile
          />
        </ContentSection>
      </UltraWideBgSection>
      <UltraWideBgSection>
        <ContentSection>
          <HalvedContentSection
            leftContent={
              <>
                <Label>Environmental Impact</Label>
                <Heading level="h3" marginTop={2}>
                  Competitive Advantage Over Diesel and Electric Alternatives
                </Heading>
                <SectionDescription marginTop={2}>
                  While electric vehicles (EVs) are often seen as the future of
                  transport, their adoption in long-haul logistics faces
                  significant challenges. In contrast, BioLNG offers a
                  ready-to-use, scalable, and cost-effective alternative that
                  provides immediate decarbonisation benefits without requiring
                  major infrastructure overhauls.
                </SectionDescription>
              </>
            }
            rightContent={
              <GridLayout
                items={[
                  ...ChallengesOfEVsInFreight.map((item) => {
                    return <StyledCard {...item} key={item.title} danger />;
                  }),
                  <ImageCard key="image" image={TruckBottomAngleView} />,
                ]}
                itemsPerRow={2}
              />
            }
          />
        </ContentSection>
      </UltraWideBgSection>

      <ContentSection>
        <HalvedImageSection
          image={TruckBlurredBg}
          imagePosition="right"
          content={
            <>
            <Heading level="h6" tga="h4">BioLNG is rapidly positioning itself as a cornerstone of sustainable long-haul transport.</Heading>
              <SectionDescription marginTop={2}>
                Its ability to leverage existing LNG infrastructure, combined with its environmental and
                economic advantages, makes it one of the most viable
                alternatives for decarbonising freight logistics. As government
                policies continue to support its adoption and infrastructure
                expands, BioLNG is set to play a crucial role in the future of
                sustainable mobility.
              </SectionDescription>
              <SectionDescription marginTop={2}>
                To learn more about BioLNG solutions and how they can benefit
                your logistics operations,{" "}
                <Link
                  href={`mailto:${CompanyInfo.email}`}
                  sx={{ color: "inherit" }}
                >
                  contact us
                </Link>{" "}
                today.
              </SectionDescription>
            </>
          }
        />
      </ContentSection>
    </>
  );
}
